import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/dentlab/src/components/ui/alert-dialog";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { PatientIdentityField } from "@/dentlab/src/store/Jobs/job-info.store";

const FIELD_LABELS: Record<PatientIdentityField, string> = {
	first_name: "Vorname",
	last_name: "Nachname",
	code: "Code",
	birth_date: "Geburtsdatum",
};

export const PatientDivergenceDialog: React.FC = () => {
	const {
		patientDivergence,
		dismissPatientDivergence,
		switchPatientFromDivergence,
		job,
	} = useJobStore((state) => ({
		patientDivergence: state.patientDivergence,
		dismissPatientDivergence: state.dismissPatientDivergence,
		switchPatientFromDivergence: state.switchPatientFromDivergence,
		job: state.job,
	}));
	const currentPatient = usePatientStore((state) =>
		job?.patient_id ? state.patientsLookup[job.patient_id] : null
	);

	const open = !!patientDivergence;

	if (!patientDivergence) {
		return (
			<AlertDialog open={false}>
				<AlertDialogContent />
			</AlertDialog>
		);
	}

	const patientLabel = currentPatient
		? [
				(currentPatient as PatientEntityType).first_name,
				(currentPatient as PatientEntityType).last_name,
			]
				.filter(Boolean)
				.join(" ") || "aktueller Patient"
		: "aktueller Patient";

	const handleSwitch = () => {
		switchPatientFromDivergence(
			patientDivergence.field,
			patientDivergence.typedValue
		);
	};

	return (
		<AlertDialog
			open={open}
			onOpenChange={(next) => {
				if (!next) dismissPatientDivergence();
			}}
		>
			<AlertDialogContent data-testid="patient-divergence-dialog">
				<AlertDialogHeader>
					<AlertDialogTitle>
						Möglicher Patientenwechsel
					</AlertDialogTitle>
					<AlertDialogDescription asChild>
						<div className="space-y-2">
							<div>
								<span className="font-medium">
									{FIELD_LABELS[patientDivergence.field]}:
								</span>{" "}
								"{patientDivergence.originalValue}" →{" "}
								<span className="font-medium">
									"{patientDivergence.typedValue}"
								</span>
							</div>
							<div className="text-sm text-muted-foreground">
								Soll der aktuelle Patient <b>{patientLabel}</b>{" "}
								mit diesen Werten bearbeitet werden, oder gehört
								der getippte Wert zu einem anderen Patienten?
								Bei "Anderen Patienten anlegen" bleibt der
								aktuelle Patient unverändert und es wird ein
								neuer Patient als Kopie der aktuellen Daten
								(inkl. Adresse, AHV, etc., ohne Code) angelegt.
							</div>
						</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel
						onClick={dismissPatientDivergence}
						aria-label="Aktuellen Patienten bearbeiten"
						data-testid="patient-divergence-dismiss"
					>
						Aktuellen Patienten bearbeiten
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={handleSwitch}
						aria-label="Anderen Patienten anlegen"
						data-testid="patient-divergence-switch"
					>
						Anderen Patienten anlegen
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
